import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { StaticPage, fetchPublicStaticPage } from '@/services/staticPages';

export default function StaticPageView() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [item, setItem] = useState<StaticPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!slug) {
      navigate('/', { replace: true });
      return;
    }
    setLoading(true);
    setNotFound(false);
    fetchPublicStaticPage(slug)
      .then((data) => {
        if (!alive) return;
        if (!data) {
          setNotFound(true);
          setItem(null);
          return;
        }
        setItem(data);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [slug, navigate]);

  useEffect(() => {
    if (!item) return;
    const previousTitle = document.title;
    document.title = `${item.title}｜富聆聽健康`;
    return () => {
      document.title = previousTitle;
    };
  }, [item]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="mx-auto max-w-4xl px-6 py-10 md:px-10 md:py-16 lg:px-16">
            <div className="h-4 w-40 animate-pulse rounded bg-[#f1ece4]" />
            <div className="mt-5 h-8 w-3/4 animate-pulse rounded bg-[#f1ece4]" />
            <div className="mt-8 space-y-4">
              <div className="h-5 animate-pulse rounded bg-[#f1ece4]" />
              <div className="h-5 w-5/6 animate-pulse rounded bg-[#f1ece4]" />
              <div className="h-5 w-2/3 animate-pulse rounded bg-[#f1ece4]" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound || !item) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="mx-auto max-w-4xl px-6 py-16 text-center md:px-10 lg:px-16">
            <h1 className="text-2xl font-bold text-[#1B3D2A] md:text-3xl">找不到這個頁面</h1>
            <p className="mt-3 text-[#9A8A7A]">您要查看的內容可能已經下架或不存在。</p>
            <button
              onClick={() => navigate('/')}
              className="mt-8 inline-flex items-center gap-2 rounded-xl bg-[#1B3D2A] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[#143026]"
              type="button"
            >
              <i className="ri-home-4-line" />
              回首頁
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="mx-auto max-w-4xl px-6 py-8 md:px-10 md:py-12 lg:px-16">
          {/* 麵包屑 */}
          <div className="flex flex-wrap items-center gap-1.5 text-sm text-[#9A8A7A]">
            <button type="button" onClick={() => navigate('/')} className="hover:text-[#1B3D2A] transition-colors">首頁</button>
            <i className="ri-arrow-right-s-line" />
            <span className="text-[#1B3D2A]">{item.title}</span>
          </div>

          {/* 標頭 */}
          <div className="mt-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <h1 className="text-xl md:text-3xl font-bold leading-snug text-[#1B3D2A]">{item.title}</h1>
            {item.updatedAt && (
              <span className="shrink-0 text-sm text-[#9A8A7A] md:pt-1">最後更新：{item.updatedAt.slice(0, 10)}</span>
            )}
          </div>

          <div className="my-6 h-px bg-[#E8E0D6]" />

          {/* 內容 */}
          <div
            className="prose prose-lg max-w-none text-gray-700 prose-headings:text-[#1B3D2A] prose-a:text-[#1B3D2A] prose-img:rounded-xl prose-iframe:aspect-video prose-iframe:w-full prose-iframe:rounded-xl"
            dangerouslySetInnerHTML={{ __html: item.content || '' }}
          />

          {/* 底部導航 */}
          <div className="mt-10 pt-8 border-t border-[#E8E0D6] flex flex-col sm:flex-row items-center justify-between gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-[#1B3D2A] text-sm font-medium flex items-center gap-2 hover:underline py-2"
            >
              <i className="ri-arrow-left-line" /> 返回首頁
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="bg-[#1B3D2A] text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-[#143026] transition-colors whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-customer-service-2-line" />
              聯絡我們
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
